/**
 * Ein Objekt das eine Kalenderwoche repräsentiert.
 * @property weekNumber - Die Nummer der Kalenderwoche.
 * @property weekStartDate - Das Startdatum der Kalenderwoche (Montag).
 * @property weekEndDate - Das Enddatum der Kalenderwoche (Freitag).
 * @property displayText - Ein formatierter Text, der die Kalenderwoche und das Datum anzeigt.
 */
export interface CalendarWeekOption {
  weekNumber: number;
  weekStartDate: Date;
  weekEndDate: Date;
  displayText: string;
}

/**
 * Ermittelt die Kalenderwoche für ein gegebenes Datum.
 * @param date Das Datum, für das die Kalenderwoche ermittelt werden soll.
 */
export function getCalendarWeek(date: Date | string): number {
  const parsedDate = new Date(date);
  const tempDate = new Date(Date.UTC(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate()));
  const dayNum = tempDate.getUTCDay() || 7;
  tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
  return Math.ceil(((tempDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('de-DE');
}

/**
 * Generiert eine Liste von Kalenderwochen-Optionen.
 *
 * Die Wochen beginnen am Montag und enden am Freitag.
 *
 * Die Optionen beginnen mit der aktuellen Woche oder dem Start-Datum.
 * Wenn das Anfangsdatum ein Samstag oder Sonntag sind, wird dennoch die Woche des Datums als Startwert verwendet, nicht die darauffolgende.
 * @param count Anzahl der zu generierenden Kalenderwochen (default: 10)
 * @param startDate (optional) Start-Datum für die Generierung der Kalenderwochen. Wenn nicht angegeben, wird das aktuelle Datum verwendet.
 */
export function generateCalendarWeeks(count = 10, startDate: Date | string = null): CalendarWeekOption[] {
  const result: CalendarWeekOption[] = [];
  const startingDate = startDate ? new Date(startDate) : new Date();
  let currentMonday = getMonday(startingDate);

  for (let i = 0; i < count; i++) {
    const start = new Date(currentMonday);
    const end = new Date(currentMonday);
    end.setDate(end.getDate() + 4);

    const weekNumber = getCalendarWeek(start);

    result.push({
      weekNumber,
      weekStartDate: start,
      weekEndDate: end,
      displayText: `KW ${weekNumber} | ${formatDate(start)} - ${formatDate(end)}`
    });

    currentMonday.setDate(currentMonday.getDate() + 7);
  }

  return result;
}
